import type { StreamEntitlement } from "../types/billing";
import type {
  ICreatePlaylistRequest,
  ILiveStreamScheduledShow,
  IScheduledShow,
  IUpdateLiveStreamRequest,
} from "../types/interfaces";
import {
  LiveStreamJingleEveryTypeEnum,
  LiveStreamOrientationEnum,
  LiveStreamTypeEnum,
} from "../types/models";
import type { LiveStreamForm } from "../redux/Slices/LiveStreamSlice";
import { builderCapabilities } from "./builderCapabilities";

export interface SettingsBodyIds {
  videoId?: string;
  jinglePlaylistId?: string;
  jingleVideoId?: string;
  secondaryVideoId?: string;
}

/**
 * The PUT /live-stream/:id body. The api leaves an omitted key unchanged
 * (A41), so an emptied jingle or secondary list is sent as an explicit null.
 * Keys for features the plan lacks are still omitted: the api refuses them.
 */
export function buildLiveStreamSettingsBody({
  id,
  form,
  entitlement,
  ids,
  liveStreamScheduledShows,
  primaryPlaylist,
}: {
  id: string;
  form: LiveStreamForm;
  entitlement: StreamEntitlement;
  ids: SettingsBodyIds;
  liveStreamScheduledShows: ILiveStreamScheduledShow[];
  primaryPlaylist?: ICreatePlaylistRequest;
}): IUpdateLiveStreamRequest {
  const caps = builderCapabilities(entitlement);
  if (form.streamType === LiveStreamTypeEnum.playlist && !primaryPlaylist) {
    throw new Error("PRIMARY_PLAYLIST_REQUIRED");
  }
  return {
    id,
    streamingPlatformsIds: form.streamingPlatformsIds,
    ...(form.streamType === LiveStreamTypeEnum.video
      ? { videoId: ids.videoId }
      : { primaryPlaylist }),
    termsAgreedAt: form.termsAgreedAt!,
    contentAgreedAt: form.contentAgreedAt!,
    startDate: form.startDate?.toDate(),
    endDate: form.endDate?.toDate(),
    startOption: form.startOption,
    endOption: form.endOption,
    playbackOrder: form.playbackOrder,
    videoLeft: form.videoLeft,
    videoTop: form.videoTop,
    // Decision #1: Basic (v2 worker, streamCopy) is 1920×1080 horizontal only.
    orientation: caps.canVertical
      ? form.orientation
      : LiveStreamOrientationEnum.horizontal,
    crfMode: form.crfMode,
    ...(caps.canVideoFade
      ? {
          videoFadeInDuration: form.videoFadeInDuration,
          videoFadeOutDuration: form.videoFadeOutDuration,
        }
      : {}),
    liveStreamScheduledShows,
    // Output settings are locked to the plan on Basic; the api refuses them (F13).
    ...(entitlement.outputSettingsEditable
      ? {
          videoWidth: form.videoWidth,
          videoHeight: form.videoHeight,
          videoBitrate: form.videoBitrate,
          videoFPS: form.videoFPS,
          audioBitrate: form.audioBitrate,
        }
      : {}),
    ...(entitlement.features.jingles
      ? {
          jingleEvery: form.jingleEvery,
          jingleEveryType: LiveStreamJingleEveryTypeEnum.songs,
          jinglePlaybackOrder: form.jinglePlaybackOrder,
          jinglePlaylistId: ids.jinglePlaylistId ?? null,
          jingleVideoId: ids.jingleVideoId ?? null,
        }
      : {}),
    ...(entitlement.features.secondaryVideo
      ? {
          secondaryVideoId: ids.secondaryVideoId ?? null,
          secondaryVideoVolume: Math.min(
            100,
            Math.max(0, Math.round(form.secondaryVideoVolume))
          ),
        }
      : {}),
    ...(entitlement.features.audioMixing
      ? {
          audioMixingEnabled: form.audioMixingEnabled,
          audioMixingDuration: form.audioMixingDuration,
        }
      : { audioMixingEnabled: false }),
  };
}

/**
 * B4: the shows as the pre-check sends them. A new show's playlist/video is
 * created later in the save, so it goes without a content id; the api still
 * checks its time, its name and the plan gate.
 */
export function precheckScheduledShows(
  shows: IScheduledShow[],
  streamType: LiveStreamTypeEnum | undefined,
  enabled: boolean
): ILiveStreamScheduledShow[] {
  if (!enabled) return [];
  return shows.map((show) => ({
    name: show.name,
    startMinuteOfDay: show.startMinuteOfDay,
    timezone: show.timezone,
    switchMode: show.switchMode || "wait",
    ...(show.id.startsWith("new")
      ? {}
      : streamType === LiveStreamTypeEnum.video
      ? { videoId: show.videoId }
      : { playlistId: show.playlistId }),
  }));
}

/** B4: reject an empty show before any content write. */
export function findEmptyScheduledShow(
  shows: IScheduledShow[],
  enabled: boolean
): IScheduledShow | undefined {
  return enabled ? shows.find((show) => show.items.length === 0) : undefined;
}
